import { Dispatch } from 'redux';
import BaseAction from 'src/models/common/BaseAction';
import LoginDTO from 'src/models/account/LoginDTO';
import RegisterDTO from 'src/models/account/RegisterDTO';
import UserMeInfo from 'src/models/account/UserMeInfo';
import AccountApiController from 'src/api/AccountApiController';
import UserStatusDTO from 'src/models/user/UserStatusDTO';
import ConfigureHistory from '../core/ConfigureHistory';
import Routes from '../core/Routes';
import CheckpointAction, { ResetCheckpointAction } from "src/redux/CheckpointActions";
import { ReduxStoreModel } from "src/redux/ReduxModel";
import ConstantAction from "src/redux/ConstantActions";
import { Checkpoints } from "src/constants/Checkpoints";
import { Planets } from "src/constants/Planets";
import FullscreenApi from 'src/utils/FullscreenHelper';
import DebugUtil from "src/utils/DebugUtil";
import GenericBaseResponseDTO from "src/models/validation/GenericBaseResponseDTO";
import SimpleCheckpointStatusDTO from "src/models/frontend/redux/SimpleCheckpointStatusDTO";
import { HttpApiResult } from "src/api/HttpApi";
import { ResetConfigAction } from "src/redux/ConfigActions";
import { addOtherDefaultTrackingProperties } from 'src/providers/TelemetryService';
import EmulationDTO from 'src/models/account/EmulationDTO';
import { datadogRum } from '@datadog/browser-rum';

export interface LoginUserAction extends BaseAction { type: 'LOGIN_USER'; data: UserMeInfo; }
export interface RegisterUserAction extends BaseAction { type: 'REGISTER_USER'; data: UserMeInfo; }
export interface UpdateUserAction extends BaseAction { type: 'UPDATE_USER'; data: UserMeInfo; }
export interface UpdateStateUserAction extends BaseAction { type: 'UPDATE_STATE_USER'; data: KnownUserStates; }
export interface UpdateErrorUserAction extends BaseAction { type: 'UPDATE_ERROR_USER'; data: string; }
export interface ResetUserAction extends BaseAction { type: 'RESET_USER'; }
export interface UpdateEmulateUserAction extends BaseAction { type: 'UPDATE_EMULATE_INFO_USER'; data: EmulationDTO; }


// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type KnownAction = LoginUserAction | UpdateUserAction | UpdateStateUserAction | UpdateErrorUserAction | ResetUserAction | UpdateEmulateUserAction;

/** Used in the UserReducer
 *
 * There are a few paths that are valid. All start with none, which is a fresh login session.
 *
 * Normal
 *
 *      None -> Loading -> Finished / Failed
 * Emulation
 *
 *      Finished -> Emulation-Loading -> Finished / Failed
 * "emulation-loading" is a special state that will allow other parts of the app to show a loading indication without causing LayoutComponent to render a spinner
*/
export type KnownUserStates = "none" | "loading" | "emulation-loading" | "failed" | "finished";

export type EmulateUserType = "school" | "state" | "district" | "cancel";

export default class UserAction {
    constructor() {
        // Dont be that guy
        throw new Error("NOOOO");
    }

    public static Register(dispatch: Dispatch<ReduxStoreModel>, request: RegisterDTO) {
        if (request == null) {
            return;
        }

        dispatch({ type: "UPDATE_STATE_USER", data: "loading" } as UpdateStateUserAction);
        return AccountApiController.register(request, { ignoreAuthenticationErrors: true })
            .then(result => {
                // Check failure since there are 2 conditions for it and either one will cause failure of the login event
                if (!result.ok || !result.data.isSuccess) {
                    return this.RegisterOnError(dispatch, result);
                }
                return this.RegisterOnSuccess(dispatch, result);
            });
    }

    public static Login(dispatch: Dispatch<ReduxStoreModel>, request: LoginDTO) {
        if (request == null) {
            return;
        }

        dispatch({ type: "UPDATE_STATE_USER", data: "loading" } as UpdateStateUserAction);
        return AccountApiController.login(request, { ignoreAuthenticationErrors: true })
            .then(result => {
                // Check failure since there are 2 conditions for it and either one will cause failure of the login event
                if (!result.ok || !result.data.isSuccess) {
                    return this.LoginOnError(dispatch, result);
                }
                return this.LoginOnSuccess(dispatch, result);
            });
    }

    /**
     * Login using the browser cookie. This will run through the login process as if we had logged in
     *
     * Returns a `Promise<bool>` indicating if the login was a success
     *
     * @param {Dispatch<ReduxStoreModel>} dispatch The dispatch object passed in from `mapDispatchToProps()`
     */
    public static SoftLogin(dispatch: Dispatch<ReduxStoreModel>): Promise<boolean> {
        dispatch({ type: "UPDATE_STATE_USER", data: "loading" } as UpdateStateUserAction);
        return AccountApiController.me({ ignoreAuthenticationErrors: true })
            .then(result => {
                // Check failure since there are 2 conditions for it and either one will cause failure of the login event
                if (!result.ok || !result.data.isSuccess) {
                    return this.LoginOnError(dispatch, result);
                }

                return this.LoginOnSuccess(dispatch, result);
            });
    }

    private static RegisterOnSuccess(dispatch: Dispatch<ReduxStoreModel>, result: HttpApiResult<GenericBaseResponseDTO<UserMeInfo>>) {
        // The only thing coming in here is going to be a success
        dispatch({ type: "UPDATE_USER", data: result.data.data } as UpdateUserAction);
        dispatch({ type: "UPDATE_ERROR_USER", data: "" } as UpdateErrorUserAction);

        let checkpointPromise = CheckpointAction.LoadUserStatus(dispatch);
        let constantsPromise = ConstantAction.LoadConstants(dispatch);

        return Promise.all([checkpointPromise, constantsPromise]).then(x => {
            // Combine all results together into a single T/F answer
            let result: boolean = x.reduce((first, second) => {
                return first && second;
            }, true);

            dispatch({ type: "UPDATE_STATE_USER", data: result ? "finished" : "failed" } as UpdateStateUserAction);
            return result;
        });
    }

    private static RegisterOnError(dispatch: Dispatch<ReduxStoreModel>, result: HttpApiResult<GenericBaseResponseDTO<UserMeInfo>>) {
        // Failures in the http request or register request come here
        // Failed results have no error message, use default. Else, use the errorMessage provided defaulting to "" if null
        let errorMessage = !result.ok ? "account.register.registerUnknownFailure" : result.data.errorMessage || "";

        dispatch({ type: "UPDATE_USER", data: {} } as UpdateUserAction);
        dispatch({ type: "UPDATE_ERROR_USER", data: errorMessage } as UpdateErrorUserAction);
        dispatch({ type: "UPDATE_STATE_USER", data: "failed" } as UpdateStateUserAction);
        return false;
    }

    private static LoginOnSuccess(dispatch: Dispatch<ReduxStoreModel>, result: HttpApiResult<GenericBaseResponseDTO<UserMeInfo>>) {
        // The only thing coming in here is going to be a success
        dispatch({ type: "UPDATE_USER", data: result.data.data } as UpdateUserAction);
        dispatch({ type: "UPDATE_ERROR_USER", data: "" } as UpdateErrorUserAction);

        let checkpointPromise = CheckpointAction.LoadUserStatus(dispatch);
        let constantsPromise = ConstantAction.LoadConstants(dispatch);
        let emulationPromise = AccountApiController.getCurrentEmulationInfo();

        if(result.data != null && result.data.data != null) {
            const userInfo = result.data.data;
            const customProperties = {
                clientId: userInfo.clientId,
                stateId: userInfo.stateId,
                districtId: userInfo.districtId,
                schoolId: userInfo.schoolId,
                clientName: userInfo.clientName,
                stateName: userInfo.stateName,
                districtName: userInfo.districtName,
                schoolName: userInfo.schoolName,
                userId: userInfo.userId,
                gradeId: userInfo.gradeId
            };

            datadogRum.setUser({
                id: userInfo.userId.toString(),
                userFullName: `${userInfo.firstName} ${userInfo.lastName}`,
                email: userInfo.email,
                username: userInfo.email,
                organizationId: userInfo.schoolId != null ? userInfo.schoolId : userInfo.districtId != null ? userInfo.districtId : userInfo.stateId != null ? userInfo.stateId : null,
                organizationName: userInfo.schoolId != null ? userInfo.schoolName : userInfo.districtId != null ? userInfo.districtName : userInfo.stateId != null ? userInfo.stateName : null
            });

            addOtherDefaultTrackingProperties(customProperties);
        }

        return Promise.all([checkpointPromise, constantsPromise, emulationPromise]).then(x => {
            // Combine the first 2 results together into a single T/F answer
            let result: boolean = [x[0], x[1]].reduce((first, second) => {
                return first && second;
            }, true);

            // Update emulation info
            dispatch({ type: "UPDATE_EMULATE_INFO_USER", data: x[2].data } as UpdateEmulateUserAction);
            dispatch({ type: "UPDATE_STATE_USER", data: result ? "finished" : "failed" } as UpdateStateUserAction);
            return result;
        });
    }

    private static LoginOnError(dispatch: Dispatch<ReduxStoreModel>, result: HttpApiResult<GenericBaseResponseDTO<UserMeInfo>>) {
        // Failures in the http request or login request come here
        // Failed results have no error message, use default. Else, use the errorMessage provided defaulting to "" if null
        let errorMessage = !result.ok ? "account.login.loginUnknownFailure" : result.data.errorMessage || "";

        dispatch({ type: "UPDATE_USER", data: {} } as UpdateUserAction);
        dispatch({ type: "UPDATE_ERROR_USER", data: errorMessage } as UpdateErrorUserAction);
        dispatch({ type: "UPDATE_STATE_USER", data: "failed" } as UpdateStateUserAction);
        return false;
    }

    public static LogoutUser(dispatch: Dispatch<ReduxStoreModel>): Promise<void> {
        return AccountApiController.logout().then(result => {
            if (result.ok) {
                dispatch({ type: "RESET_USER" } as ResetUserAction);
                dispatch({ type: "RESET_CONFIG" } as ResetConfigAction);
                dispatch({ type: "RESET_CHECKPOINT" } as ResetCheckpointAction);
            }
        });
    }

    /**
     * Emulates a user given the emulation type and ID. Returns a promise indicating success or failure.
     *
     * Does not redirect based on new role, that's up to you hombre. You must also properly handle messages which will be passed back in the `.then()` clause
     */
    public static EmulateUser(dispatch: Dispatch<ReduxStoreModel>, emulationType: EmulateUserType, id?: number): Promise<boolean> {
        // Pick which api we need to hit
        let apiPromise: Promise<HttpApiResult<void>>;
        switch (emulationType) {
            case "school":
                apiPromise = AccountApiController.emulateSchool(id);
                break;
            case "state":
                apiPromise = AccountApiController.emulateState(id);
                break;
            case "district":
                apiPromise = AccountApiController.emulateDistrict(id);
                break;
            case "cancel":
                apiPromise = AccountApiController.stopEmulation();
                break;
            default:
                // JB - Clarification: The following line is fucking magic
                const exhaustiveCheck: never = emulationType;
        }

        // This is where the newness starts
        // Emulate only needs a few things, so lets just call them all from this Action
        dispatch({ type: "UPDATE_STATE_USER", data: "emulation-loading" } as UpdateStateUserAction);

        return apiPromise
            .then(result => AccountApiController.me({ ignoreAuthenticationErrors: true }))
            .then(result => {
                // Check failure since there are 2 conditions for it and either one will cause failure of the login event
                if (!result.ok || !result.data.isSuccess) {
                    return this.LoginOnError(dispatch, result);
                }

                if(result.data != null && result.data.data != null) {
                    const userInfo = result.data.data;
                    const customProperties = {
                        clientId: userInfo.clientId,
                        stateId: userInfo.stateId,
                        districtId: userInfo.districtId,
                        schoolId: userInfo.schoolId,
                        clientName: userInfo.clientName,
                        stateName: userInfo.stateName,
                        districtName: userInfo.districtName,
                        schoolName: userInfo.schoolName,
                        userId: userInfo.userId,
                        gradeId: userInfo.gradeId
                    };
                    addOtherDefaultTrackingProperties(customProperties);
                }

                // The only thing coming in here is going to be a success
                dispatch({ type: "UPDATE_USER", data: result.data.data } as UpdateUserAction);
                dispatch({ type: "UPDATE_ERROR_USER", data: "" } as UpdateErrorUserAction);

                let checkpointPromise = CheckpointAction.LoadUserStatus(dispatch);
                let constantsPromise = ConstantAction.LoadConstantsEmulation(dispatch);
                let emulationPromise = AccountApiController.getCurrentEmulationInfo();

                return Promise.all([checkpointPromise, constantsPromise, emulationPromise]).then(x => {
                    // Combine the first 2 results together into a single T/F answer
                    let result: boolean = [x[0], x[1]].reduce((first, second) => {
                        return first && second;
                    }, true);

                    // Update emulation info
                    dispatch({ type: "UPDATE_EMULATE_INFO_USER", data: x[2].data } as UpdateEmulateUserAction);
                    dispatch({ type: "UPDATE_STATE_USER", data: result ? "finished" : "failed" } as UpdateStateUserAction);
                    return result;
                });
            });
    }

    /**
     * Updated for up/down movement
     *
     * Emulates a user given the emulation type and ID. Returns a promise indicating success or failure.
     *
     * Does not redirect based on new role, that's up to you hombre. You must also properly handle messages which will be passed back in the `.then()` clause
     */
    public static EmulateUserV2(dispatch: Dispatch<ReduxStoreModel>, id: number, emulationType: EmulateUserType): Promise<boolean> {
        // This is where the newness starts
        // Emulate only needs a few things, so lets just call them all from this Action
        dispatch({ type: "UPDATE_STATE_USER", data: "emulation-loading" } as UpdateStateUserAction);

        return AccountApiController.emulate(id, emulationType)
            .then(result => {
                // Check failure since there are 2 conditions for it and either one will cause failure of the login event
                if (!result.ok || !result.data.isSuccess) {
                    return this.LoginOnError(dispatch, result);
                }

                if (result.data != null && result.data.data != null) {
                    const userInfo = result.data.data;
                    const customProperties = {
                        clientId: userInfo.clientId,
                        stateId: userInfo.stateId,
                        districtId: userInfo.districtId,
                        schoolId: userInfo.schoolId,
                        clientName: userInfo.clientName,
                        stateName: userInfo.stateName,
                        districtName: userInfo.districtName,
                        schoolName: userInfo.schoolName,
                        userId: userInfo.userId,
                        gradeId: userInfo.gradeId
                    };
                    addOtherDefaultTrackingProperties(customProperties);
                }

                // The only thing coming in here is going to be a success
                dispatch({ type: "UPDATE_USER", data: result.data.data } as UpdateUserAction);
                dispatch({ type: "UPDATE_ERROR_USER", data: "" } as UpdateErrorUserAction);

                let checkpointPromise = CheckpointAction.LoadUserStatus(dispatch);
                let constantsPromise = ConstantAction.LoadConstantsEmulation(dispatch);
                let emulationPromise = AccountApiController.getCurrentEmulationInfo();

                return Promise.all([checkpointPromise, constantsPromise, emulationPromise]).then(x => {
                    // Combine the first 2 results together into a single T/F answer
                    let result: boolean = [x[0], x[1]].reduce((first, second) => {
                        return first && second;
                    }, true);

                    // Update emulation info
                    dispatch({ type: "UPDATE_EMULATE_INFO_USER", data: x[2].data } as UpdateEmulateUserAction);
                    dispatch({ type: "UPDATE_STATE_USER", data: result ? "finished" : "failed" } as UpdateStateUserAction);
                    return result;
                });
            })
            .catch(error => {
                // I don't care right now
                return this.LoginOnError(dispatch, { ok: false } as any);
            });
    }

    /** Stops the user from emulating. You must redirect the user when completed */
    public static StopEmulation(dispatch: Dispatch<ReduxStoreModel>) {
        // Cancel is essentially just "emulate yourself"
        return this.EmulateUser(dispatch, "cancel");
    }

    public static SetLogin(dispatch: Dispatch<ReduxStoreModel>, request: UserMeInfo) {
        dispatch({ type: "UPDATE_USER", data: request } as UpdateUserAction);
    }
}

export function updateUserError(dispatch: Dispatch<any>, message: string) {
    dispatch({ type: "UPDATE_ERROR_USER", data: message } as UpdateErrorUserAction);
}




// WEBPACK FOOTER //
// ./src/redux/UserActions.ts